import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {BoardFilters, PipelineId, TeamMember} from '../../domain/entities/Pipeline.ts'
import {CONVERSION_GROUPS, ConversionBoardFilters} from '../../domain/entities/ConversionPipeline.ts'
import {useB2BAgencies} from '../../hooks/useB2BAgencies.ts'
import {dateInputToUtcMidnight, DEFAULT_MARKETS, millisToDateInput} from './pipelineView.ts'
import {MultiSelectFilter} from './MultiSelectFilter.tsx'

const SEARCH_DEBOUNCE_MS = 300

const SELECT = 'h-9 pl-3 pr-8 rounded-full border border-neutralBlack-200 bg-white text-[13px] font-medium text-neutralBlack-900 hover:border-neutralBlack-400 focus:outline-none focus-visible:border-neutralBlack-900 transition-colors duration-200 ease-out'
const ACTIVE = 'border-neutralBlack-900'

type FilterSelectProps = {
    label: string
    value: string | undefined
    onChange: (value: string | undefined) => void
    options: {value: string; label: string}[]
}

// A native select per filter: keyboard and screen-reader friendly as is.
const FilterSelect: React.FC<FilterSelectProps> = ({label, value, onChange, options}) => (
    <label className="inline-flex items-center">
        <span className="sr-only">{label}</span>
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}
                className={`${SELECT} ${value ? ACTIVE : ''}`}>
            <option value="">{label}</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    </label>
)

// Both boards' filters: Activation adds the route, Conversion the mode, the
// editor, the channel and the group.
type AnyBoardFilters = BoardFilters & ConversionBoardFilters

export const BoardFiltersBar: React.FC<{
    pipeline?: PipelineId
    filters: AnyBoardFilters
    team: TeamMember[]
    onChange: (filters: AnyBoardFilters) => void
}> = ({pipeline = 'activation', filters, team, onChange}) => {
    const {t} = useTranslation('dashboard')
    const {agencies, facets} = useB2BAgencies()
    const [search, setSearch] = useState(filters.q ?? '')

    // Debounced free-text search (q).
    useEffect(() => {
        const handle = setTimeout(() => {
            const q = search.trim() || undefined
            if (q !== filters.q) onChange({...filters, q})
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(handle)
        // Only the typed text drives this effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search])

    const set = (patch: Partial<AnyBoardFilters>) => onChange({...filters, ...patch})
    const markets = facets?.markets?.length ? facets.markets : DEFAULT_MARKETS
    const members = team.map((m) => ({value: m.id, label: m.name}))
    const hasFilters = Object.values(filters).some((v) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0))

    return (
        <div className="flex items-center flex-wrap gap-2.5" role="search" aria-label={t('pipelines.filters.label')}>
            <label className="inline-flex items-center">
                <span className="sr-only">{t('pipelines.filters.search')}</span>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder={t('pipelines.filters.searchPlaceholder')}
                       className="h-9 w-[220px] px-3.5 rounded-full border border-neutralBlack-200 bg-white text-[13px] text-neutralBlack-900 placeholder:text-neutralBlack-600 focus:outline-none focus:border-neutralBlack-900"/>
            </label>
            <MultiSelectFilter label={t('pipelines.filters.market')} values={filters.market}
                               onChange={(market) => set({market})}
                               options={markets.map((m) => ({value: m, label: m}))}/>
            <MultiSelectFilter label={t('pipelines.filters.team')} values={filters.assignee}
                               onChange={(assignee) => set({assignee})}
                          options={[
                              {value: 'automatic', label: t('pipelines.automatic')},
                              {value: 'unassigned', label: t('pipelines.unassigned')},
                              ...members,
                          ]}/>
            <FilterSelect label={t('pipelines.filters.agency')} value={filters.agencyId}
                          onChange={(agencyId) => set({agencyId})}
                          options={[...agencies].sort((a, b) => a.name.localeCompare(b.name)).map((a) => ({value: a.id, label: a.name}))}/>
            <FilterSelect label={t('pipelines.filters.accountManager')} value={filters.accountManager}
                          onChange={(accountManager) => set({accountManager})} options={members}/>
            {pipeline === 'activation' ? (
                <FilterSelect label={t('pipelines.filters.route')} value={filters.route}
                              onChange={(route) => set({route: route as BoardFilters['route']})}
                              options={[{value: 'A', label: t('activation.route.badge', {route: 'A'})}, {value: 'C1', label: t('activation.route.badge', {route: 'C1'})}]}/>
            ) : (
                <>
                    <FilterSelect label={t('pipelines.filters.mode')} value={filters.mode}
                                  onChange={(mode) => set({mode: mode as ConversionBoardFilters['mode']})}
                                  options={(['EXPERTS', 'DIY'] as const).map((m) => ({value: m, label: t(`pipelines.mode.${m}`)}))}/>
                    <MultiSelectFilter label={t('pipelines.filters.editor')} values={filters.editor}
                                       onChange={(editor) => set({editor})}
                                       options={[{value: 'unassigned', label: t('pipelines.unassigned')}, ...members]}/>
                    <FilterSelect label={t('pipelines.filters.channel')} value={filters.channel}
                                  onChange={(channel) => set({channel: channel as ConversionBoardFilters['channel']})}
                                  options={(['B2C', 'AGENCY'] as const).map((c) => ({value: c, label: t(`pipelines.salesChannel.${c}`)}))}/>
                    <FilterSelect label={t('pipelines.filters.group')} value={filters.group}
                                  onChange={(group) => set({group: group as ConversionBoardFilters['group']})}
                                  options={CONVERSION_GROUPS.map((g) => ({value: g.id, label: t(`pipelines.group.${g.id}`)}))}/>
                </>
            )}
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-neutralBlack-700">
                <label htmlFor={`pl-from-${pipeline}`}>{t('pipelines.filters.tripEnd')}</label>
                <input id={`pl-from-${pipeline}`} type="date" aria-label={t('pipelines.filters.from')}
                       value={millisToDateInput(filters.from, true)}
                       onChange={(e) => set({from: dateInputToUtcMidnight(e.target.value)})}
                       className={`h-9 px-2.5 rounded-full border bg-white text-[12.5px] ${filters.from != null ? ACTIVE : 'border-neutralBlack-200'}`}/>
                <span aria-hidden="true">–</span>
                <input type="date" aria-label={t('pipelines.filters.to')}
                       value={millisToDateInput(filters.to, true)}
                       onChange={(e) => set({to: dateInputToUtcMidnight(e.target.value)})}
                       className={`h-9 px-2.5 rounded-full border bg-white text-[12.5px] ${filters.to != null ? ACTIVE : 'border-neutralBlack-200'}`}/>
            </span>
            {hasFilters && (
                <button type="button" onClick={() => {setSearch(''); onChange({})}}
                        className="text-[12.5px] font-medium text-neutralBlack-900 underline underline-offset-[3px] px-1 hover:text-neutralBlack-600 transition-colors duration-200 ease-out">
                    {t('pipelines.filters.clear')}
                </button>
            )}
        </div>
    )
}
